import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { buyerOrdersService } from '../../../services/buyerService';

const OrderStatusTab = ({ language }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  const content = {
    en: {
      title: 'Order Status',
      noOrders: 'No orders found',
      loading: 'Loading orders...',
      filterAll: 'All',
      filterPending: 'Pending',
      filterInProgress: 'In Progress',
      filterCompleted: 'Completed',
      filterCancelled: 'Cancelled',
      orderId: 'Order ID',
      bean: 'Coffee Bean',
      quantity: 'Quantity',
      status: 'Status',
      date: 'Order Date',
      supplier: 'Supplier',
      notes: 'Notes'
    },
    zh: {
      title: '订单状态',
      noOrders: '未找到订单',
      loading: '加载订单中...',
      filterAll: '全部',
      filterPending: '待处理',
      filterInProgress: '进行中',
      filterCompleted: '已完成',
      filterCancelled: '已取消',
      orderId: '订单号',
      bean: '咖啡豆',
      quantity: '数量',
      status: '状态',
      date: '订单日期',
      supplier: '供应商',
      notes: '备注'
    }
  };

  const t = content?.[language];

  useEffect(() => {
    const controller = new AbortController();
    loadOrders(controller.signal);
    return () => controller.abort();
  }, []);

  const loadOrders = async (signal) => {
    try {
      setLoading(true);
      setError('');
      const data = await buyerOrdersService?.getAll();

      // If we had a signal, we'd pass it to the service, but since buyerOrdersService 
      // doesn't support signals yet, we just check if the signal is already aborted
      if (signal?.aborted) return;

      setOrders(data);
    } catch (err) {
      // Only set error if it's not a cancellation/abort error
      if (err?.name !== 'AbortError' && err?.message !== 'signal is aborted without reason') {
        setError(err?.message || 'Failed to load orders');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-warning',
      in_progress: 'text-primary',
      completed: 'text-success',
      cancelled: 'text-destructive'
    };
    return colors?.[status] || 'text-muted-foreground';
  };

  const getStatusBg = (status) => {
    const colors = {
      pending: 'bg-warning/10',
      in_progress: 'bg-primary/10',
      completed: 'bg-success/10',
      cancelled: 'bg-destructive/10'
    };
    return colors?.[status] || 'bg-muted';
  };

  const filteredOrders = filter === 'all'
    ? orders
    : orders?.filter(order => order?.status === filter);

  if (loading) {
    return (
      <div className="text-center py-12">
        <Icon name="Loader2" size={32} className="animate-spin text-primary mx-auto mb-4" />
        <p className="text-muted-foreground">{t?.loading}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <Icon name="AlertCircle" size={32} className="text-destructive mx-auto mb-4" />
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-heading font-bold text-foreground">{t?.title}</h2>
        <div className="flex gap-2">
          {['all', 'pending', 'in_progress', 'completed', 'cancelled']?.map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-smooth ${filter === status
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
            >
              {t?.[`filter${status?.charAt(0)?.toUpperCase() + status?.slice(1)?.replace('_', '')}`]}
            </button>
          ))}
        </div>
      </div>

      {filteredOrders?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="ShoppingCart" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{t?.noOrders}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredOrders?.map((order) => (
            <div key={order?.id} className="bg-muted/30 rounded-lg p-4 border border-border">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-sm text-muted-foreground">{t?.orderId}</p>
                  <p className="font-mono text-sm">{order?.id?.slice(0, 8)}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBg(order?.status)} ${getStatusColor(order?.status)}`}>
                  {order?.status?.replace('_', ' ')?.toUpperCase()}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{t?.bean}</p>
                  <p className="font-medium">{order?.bean?.[`name${language === 'zh' ? 'Zh' : 'En'}`]}</p>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground mb-1">{t?.quantity}</p>
                  <p className="font-medium">{order?.quantityKg} kg</p>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground mb-1">{t?.supplier}</p>
                  <p className="font-medium">{order?.bean?.supplier?.[`name${language === 'zh' ? 'Zh' : 'En'}`]}</p>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground mb-1">{t?.date}</p>
                  <p className="font-medium">{new Date(order?.createdAt)?.toLocaleDateString()}</p>
                </div>
              </div>

              {order?.message && (
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-sm text-muted-foreground mb-1">{t?.notes}</p>
                  <p className="text-sm">{order?.message}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrderStatusTab;